import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useSpring, animated } from '@react-spring/web'
import classNames from 'classnames'
import { MyIcon } from './MyIcon'
import { debounce } from 'lodash'
import styles from './AutoScroll.module.css'

/**
 *
 * @param activeIndex 选中的tab序
 * @param distanceCoefficient 前后按钮一次移动距离系数（容器宽度 * Coefficient） 默认0.5
 * @param children 节点，<div>{list.map}</div>结构，否则滚动不生效
 * @param hasArrow 需要滑动按钮
 * @param gutter 每一个item的距离，保证按钮切换时的精度
 * @returns
 */

const AutoScrollContent: React.ForwardRefRenderFunction<
  {
    tabListContainerDom?: HTMLDivElement | null
  },
  {
    activeIndex: number
    hasArrow?: boolean
    className?: string
    children?: React.ReactNode
    gutter?: number
    distanceCoefficient?: number
    // 使用 data-key 获取dom
    isUseDataKey?: boolean
    activeKey?: number
    /** 如果设计需要在右侧有一些 mask 遮罩，这个可以控制 mask 的显示状态 */
    setArriveRight?: React.Dispatch<React.SetStateAction<boolean>>
  }
> = (
  {
    activeIndex,
    hasArrow = false,
    className,
    children,
    gutter = 12,
    distanceCoefficient = 0.5,
    isUseDataKey,
    activeKey,
    setArriveRight,
  },
  ref
) => {
  const tabListContainerRef = useRef<HTMLDivElement>(null)
  //左右滑动
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(true)
  const [scrollDistance, setScrollDistance] = useState(0)

  const [{ scrollLeft }, scrollApi] = useSpring(() => ({
    scrollLeft: 0,
    config: {
      tension: 300,
      clamp: true,
    },
  }))

  const clamp = (
    position: number,
    min: number | undefined,
    max: number | undefined
  ) => {
    let ret = position
    if (min !== undefined) {
      ret = Math.max(position, min)
    }
    if (max !== undefined) {
      ret = Math.min(ret, max)
    }
    return ret
  }

  const animate = (direction?: 'LEFT' | 'RIGHT') => {
    const container = tabListContainerRef.current
    if (!container) return
    const tabWrapper = container.children.item(0) as HTMLDivElement
    if (!tabWrapper) return
    // const activeTab = tabWrapper.children.item(activeIndex) as HTMLDivElement
    const activeTab = isUseDataKey
      ? (Array.from(tabWrapper.children).find(
          item => Number(item.getAttribute('data-key')) === activeKey
        ) as HTMLDivElement)
      : (tabWrapper.children.item(activeIndex) as HTMLDivElement)
    if (!activeTab) return
    /**选中tab的offsetLeft */
    const activeTabLeft = activeTab.offsetLeft
    /**选中tab的宽度 */
    const activeTabWidth = activeTab.offsetWidth
    /**容器有效宽度 */
    const containerWidth = container.offsetWidth
    /**容器总宽度（含超出部分） */
    const containerScrollWidth = container.scrollWidth
    /**容器scroll的距离 */
    const containerScrollLeft = container.scrollLeft
    /**最大可以滚动的距离 = overflow超出部分 */
    const maxScrollDistance = containerScrollWidth - containerWidth
    setArriveRight?.(!maxScrollDistance)
    const oneceDistance = Math.round(
      (containerWidth + gutter) * distanceCoefficient
    )
    if (maxScrollDistance <= 0) return
    if (direction === 'LEFT') {
      const nextScrollLeft = clamp(
        containerScrollLeft - oneceDistance,
        0,
        maxScrollDistance
      )
      scrollApi.start({
        scrollLeft: nextScrollLeft,
        from: { scrollLeft: containerScrollLeft },
      })
    } else if (direction === 'RIGHT') {
      const nextScrollLeft = clamp(
        containerScrollLeft + oneceDistance,
        0,
        maxScrollDistance
      )
      scrollApi.start({
        scrollLeft: nextScrollLeft,
        from: { scrollLeft: containerScrollLeft },
      })
    } else {
      const nextScrollLeft = clamp(
        activeTabLeft - (containerWidth - activeTabWidth) / 2,
        0,
        maxScrollDistance
      )
      setArriveRight?.(nextScrollLeft >= maxScrollDistance)
      scrollApi.start({
        scrollLeft: nextScrollLeft,
        from: { scrollLeft: containerScrollLeft },
      })
    }
  }

  const onScroll = () => {
    if (!hasArrow) return
    const container = tabListContainerRef.current
    if (!container) return
    const containerScrollLeft = container.scrollLeft
    setScrollDistance(containerScrollLeft)
  }

  const onPrev = () => {
    if (!canPrev) return
    animate('LEFT')
  }

  const onNext = () => {
    if (!canNext) return
    animate('RIGHT')
  }

  useImperativeHandle(ref, () => ({
    tabListContainerDom: tabListContainerRef.current,
  }))

  useEffect(() => {
    const container = tabListContainerRef.current
    if (!container) return
    const containerWidth = container.offsetWidth
    const containerScrollWidth = container.scrollWidth
    const maxScrollDistance = containerScrollWidth - containerWidth
    if (scrollDistance <= 0) {
      setCanPrev(false)
      setCanNext(true)
    } else if (scrollDistance >= maxScrollDistance) {
      setCanPrev(true)
      setCanNext(false)
    } else {
      setCanPrev(true)
      setCanNext(true)
    }
  }, [scrollDistance])

  useEffect(() => {
    // 避免取消选中时，回滚到第一屏
    if (activeIndex >= 0) {
      animate()
    }
  }, [activeIndex])
  useEffect(() => {
    animate()
  }, [activeKey])

  return (
    <div className={classNames(styles.autoScrollWrap, className)}>
      <animated.div
        className={styles.autoScroll}
        ref={tabListContainerRef}
        scrollLeft={scrollLeft}
        onScroll={debounce(onScroll, 300)}
      >
        {children}
      </animated.div>
      {hasArrow && (
        <>
          <div
            className={classNames(styles.switchArrow, 'prevButton', {
              disabled: !canPrev,
            })}
            onClick={onPrev}
          >
            <MyIcon
              icon="arrow-right-simple"
              className="text-[32px] font-normal text-inherit"
            />
          </div>
          <div
            className={classNames(styles.switchArrow, 'nextButton', {
              disabled: !canNext,
            })}
            onClick={onNext}
          >
            <MyIcon
              icon="arrow-right-simple"
              className="text-[32px] font-normal text-inherit"
            />
          </div>
        </>
      )}
    </div>
  )
}
export const AutoScroll = forwardRef(AutoScrollContent)
